import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import {
  Button,
  CircularProgress,
  DialogActions,
  TextField,
} from "@mui/material";
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Box, Stack } from "@mui/system";
import Editor from "react-simple-code-editor";
import Prism from "prismjs";
import "prismjs/components/prism-sql";
import "prismjs/themes/prism.css"; // optional theme for syntax highlighting
import DataStatusAlert from "../../../../AssetManagement/Health/DataAccuracy/UI_HelpersOf_DataAccuracy/DataStatusAlert_Helpers";
import { useSelector } from "react-redux";
import { useMonitoringProcessLogs } from "../../../../../customHooks/useMonitoringProcessLogs";
import { useDispatch } from "react-redux";
import { setArgoPODLogData } from "../../../../../features/monitoringProcessLogsSlice";

interface Props {
  open: boolean;
  handleClose: () => void;
}

const DcRunIdModal: React.FC<Props> = ({ open, handleClose }) => {
  const { selectedRow, argoPODLogData } = useSelector(
    (state: any) => state.monitoringProcessLogs
  );
const dispatch = useDispatch();
  const { postArgoPOD, isLoading } = useMonitoringProcessLogs();
  const [severity, setSeverity] = useState("all");
  const [requestSql, setRequestSql] = useState("");
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSeverity(event.target.value);
  };

  const onCloseTheModal = () => {
    setSeverity("all");
    setRequestSql("");
    handleClose();
  };

  useEffect(() => {
    if (Object.keys(selectedRow).length > 0 && severity) {
      setRequestSql(""); // clear the request sql
      dispatch(setArgoPODLogData([])); // clear the argoPODLogData
      const formData = new URLSearchParams();
      formData.append("run_id", selectedRow?.run_id);
      formData.append("severity", severity);
      postArgoPOD(formData);
    }
    // eslint-disable-next-line
  }, [open, selectedRow, severity]);

  useEffect(() => {
    setRequestSql(
      argoPODLogData?.length > 0
        ? argoPODLogData.join("\n")
        : "No data to display"
    );
  }, [argoPODLogData]);

  return (
    <Dialog open={open} onClose={onCloseTheModal} fullWidth maxWidth="md">
      <DialogTitle id="alert-dialog-title">
        <Stack flexDirection={"row"} alignItems={"center"} gap={2}>
          Run ID: {selectedRow?.run_id}
          {selectedRow?.status === "SUCCESS" && (
            <DataStatusAlert status="Success" />
          )}
          {selectedRow?.status === "FAILURE" && (
            <DataStatusAlert status="Failure" />
          )}
          {selectedRow?.status === "CANCELLED" && (
            <DataStatusAlert status="Cancelled" />
          )}
          {selectedRow?.status === "RUNNING" && (
            <DataStatusAlert status="Running" />
          )}
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ padding: "16px 24px" }}>
        <DialogContentText id="alert-dialog-description">
          <TextField
            id="standard-read-only-input"
            label="Log Message"
            defaultValue={selectedRow?.message ? selectedRow?.message : "No log message to display"}
            variant="standard"
            color="secondary"
            fullWidth
            sx={{
              "& .MuiInputBase-input": {
                fontSize: "16px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "16px",
              },
              "& .MuiInput-underline:before": {
                borderBottom: "none",
              },
              "& .MuiInput-underline:after": {
                borderBottom: "none",
              },
              "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                borderBottom: "none",
              },
            }}
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
          />
          <FormControl component="fieldset" sx={{ marginTop: 1 }}>
            <Stack
              flexDirection={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <FormLabel
                component="legend"
                sx={{ marginRight: 1, color: "#00000099 !important" }}
              >
                Severity:
              </FormLabel>
              <RadioGroup
                row
                aria-label="severity"
                name="severity"
                value={severity}
                onChange={handleChange}
              >
                <FormControlLabel
                  value="all"
                  control={
                    <Radio
                      data-tracker="353"
                      sx={{
                        color: "#00000099",
                        "&.Mui-checked": { color: "#9C27B0" },
                      }}
                    />
                  }
                  label="All"
                />
                <FormControlLabel
                  value="info"
                  control={
                    <Radio
                      data-tracker="354"
                      sx={{
                        color: "#00000099",
                        "&.Mui-checked": { color: "#9C27B0" },
                      }}
                    />
                  }
                  label="Info"
                />
                <FormControlLabel
                  value="warning"
                  control={
                    <Radio
                      data-tracker="355"
                      sx={{
                        color: "#00000099",
                        "&.Mui-checked": { color: "#9C27B0" },
                      }}
                    />
                  }
                  label="Warning"
                />
                <FormControlLabel
                  value="error"
                  control={
                    <Radio
                      data-tracker="356"
                      sx={{
                        color: "#00000099",
                        "&.Mui-checked": { color: "#9C27B0" },
                      }}
                    />
                  }
                  label="Error"
                />
                <FormControlLabel
                  value="bug"
                  control={
                    <Radio
                      data-tracker="357"
                      sx={{
                        color: "#00000099",
                        "&.Mui-checked": { color: "#9C27B0" },
                      }}
                    />
                  }
                  label="Bug"
                />
              </RadioGroup>
            </Stack>
          </FormControl>
          {/* SQL Code Display */}

          <div
            style={{
              position: "relative",
              display: "inline-block",
              width: "100%",
              marginTop: "16px",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "-8px",
                left: "8px",
                background: "#f5f5f5",
                padding: "2px 6px",
                fontSize: "12px",
                borderRadius: "4px",
                zIndex: 1,
              }}
            >
              Log SQL
            </div>

            <Box
              sx={{
                height: "200px", // Set fixed height here
                overflowY: "auto", // Enable vertical scroll
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            >
              {isLoading ? (
                <Box
                  sx={{
                    height: "200px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress color="secondary" />
                </Box>
            
              ) : (
                <>
                  <Editor
                    //value="SELECT * FROM dc_get_single_object_set WHERE term='gdrive_file_details';"
                    value={requestSql}
                    onValueChange={(val) => setRequestSql(val)}
                    highlight={(code) =>
                      Prism.highlight(code, Prism.languages.sql, "sql")
                    }
                    padding={10}
                    style={{
                      fontFamily: '"Fira code", "Fira Mono", monospace',
                      fontSize: 14,
                      minHeight: "100%", // Make sure editor fills container
                      whiteSpace: "pre-wrap",
                      wordWrap: "break-word",
                    }}
                  />
                </>
              )}
            </Box>
          </div>
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ padding: "0px 24px 20px 24px" }}>
        <Button
          data-tracker="352"
          onClick={onCloseTheModal}
          color="secondary"
          variant="contained"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DcRunIdModal;
